import * as THREE from 'three';
import { coreGlow } from '../render/materials';
import { PROP } from './mech-kit';
import { TIK, TPOSE, TSTANCE } from './titan-pose';
import { buildCockpit, buildSensor } from './mech-parts';
import { buildTitanCore } from './titan-core';
import { buildTitanCannon, buildTitanPod } from './titan-arm';
import { buildTitanBack, buildTitanLegs } from './titan-legs';
import { equippedShape } from './mech-shape';
import { mergeBones } from './mech-merge';
import { newAnim } from './mech-anim';
/* ============================================================
   TITAN-3 組裝（骨架契約與另外兩台完全相同）
   ============================================================

   `root > body > { lower, torso }` 三層，與 `buildChassis0` / `buildLancer2`
   逐字相同 —— 動畫層、特效層、`mergeBones`、局外部件換裝四套系統都只認
   這個契約。三台的差異全部收在「哪些 builder」＋「pose / stance / ik」。

   比例：最矮（腿只佔 43%）、最寬（肩線 ±1.75）、腰不收。
   完整的三台對照表在 `entities/titan-pose.ts` 的檔頭。 */
export function buildTitan3(loadout?) {
  var s = loadout || {
    core: equippedShape('core', 'core-std', 'titan3'),
    cockpit: equippedShape('cockpit', 'cock-std', 'titan3'),
    thruster: equippedShape('thruster', 'thr-twin', 'titan3'),
    main: equippedShape('main', 'main-vulcan', 'titan3'),
    sensor: equippedShape('sensor', 'sen-std', 'titan3'),
    pylon: equippedShape('pylon', 'pyl-std', 'titan3')
  };
  var P = { glow: coreGlow(s.core) };
  var root = new THREE.Group();
  var parts: any = {};

  var body = new THREE.Group(); (body.userData as any).bone = 1;
  body.position.y = TSTANCE.bodyY;
  root.add(body);

  var lower = buildTitanLegs(s.thruster, P);
  (lower.userData as any).bone = 1;
  lower.rotation.y = TPOSE.hipYaw;
  parts.thruster = lower; body.add(lower);

  var torso = buildTitanCore(s.core, P);
  (torso.userData as any).bone = 1;
  torso.position.y = 1.50 - TSTANCE.bodyY;      // 腰線在世界 1.50（三台裡最低）
  torso.rotation.set(TPOSE.torsoPitch, 0, TPOSE.torsoRoll);
  parts.core = torso; body.add(torso);

  var back = buildTitanBack(s.thruster, P);
  (back.userData as any).bone = 1;
  back.position.set(0, 0.80, -0.66);
  parts.back = back; torso.add(back);

  /* 頭埋進兩片斜方肌之間（肩承座在 local 0.96、斜方肌上緣
     1.20，頭頂 1.69）。**完全沒有脖子** —— 比 LANCER-2 再誇張一階，
     這是「大塊頭」在 60px 剪影下唯一讀得到的頭部特徵。 */
  var head = buildCockpit(s.cockpit, P);
  (head.userData as any).bone = 1;
  head.position.set(0.08, 1.26, 0.30);
  head.scale.setScalar(0.94);
  head.rotation.set(TPOSE.headPitch, TPOSE.headYaw, -TPOSE.torsoRoll * 0.7);
  parts.cockpit = head; torso.add(head);

  var sen = buildSensor(s.sensor, P);
  sen.position.set(-0.24, 0.20, 0.03);
  parts.sensor = sen; head.add(sen);

  var pyl = buildTitanPod(s.pylon, P);
  (pyl.userData as any).bone = 1;
  pyl.position.set(1.22, 0.96, 0);
  pyl.rotation.set(0, TPOSE.pylonYaw, TPOSE.pylonRoll);
  parts.pylon = pyl; torso.add(pyl);

  var arm = buildTitanCannon(s.main, P);
  (arm.userData as any).bone = 1;
  arm.position.set(-1.22, 0.96, 0);
  parts.main = arm; torso.add(arm);

  var rig: any = {
    kind: 'v3', root: root, parts: parts, shapes: s,
    pose: TPOSE, stance: TSTANCE, ik: TIK,
    body: body, lower: lower, torso: torso, back: back, head: head, pylon: pyl,
    pelvis: (lower.userData as any).rig.pelvis,
    legs: (lower.userData as any).rig.legs,
    arm: (arm.userData as any).rig,
    muzzleY: (arm.userData as any).muzzle,
    nozzles: (back.userData as any).nozzles,
    vents: (back.userData as any).vents,
    glow: P.glow,
    muzzles: [], jets: []          // 舊介面的殘骸，見 `mechs.ts` 同名欄位的說明
  };
  (root.userData as any).rig = rig;

  mergeBones(root);                // **一定要在特效建立之前**（見 `mechs.ts`）
  rig.anim = newAnim();
  root.scale.setScalar(PROP.scale);
  return rig;
}
